import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime } from 'rxjs';
import moment from 'moment';
import { DashboardBaseComponent } from '../dashboard-operacional/dashboard-base';
import {
  FORMA_PAGAMENTO,
  KpiCard,
  LIMPEZAS_POR_MES,
  LIMPEZAS_POR_TERMINAL,
  LIMPEZAS_POR_TRANSPORTADORA,
  LineSeries,
  MEDIA_MENSAL_SERVICOS,
  PieSlice
} from '../dashboard-operacional/dashboard-operacional.mock';
import { DashboardService } from 'src/app/services/DashboardService/dashboard.service';
import { IGetDashboardServicosResponse } from 'src/app/services/DashboardService/dashboard.model';
import { StorageService } from 'src/app/services/StorageService/storage.service';
import { validarPermissao } from 'src/app/core/utils/permissionScreen/permissionScreen';
import { IOpcaoTela } from 'src/app/core/utils/permissionScreen/permissionScreen.types';

@Component({
  selector: 'app-dashboard-servicos',
  templateUrl: './dashboard-servicos.component.html',
  styleUrls: ['../dashboard-operacional/dashboard-operacional.component.scss']
})
export class DashboardServicosComponent extends DashboardBaseComponent implements OnInit {
  readonly dashboardTitle = 'Dashboard de Serviços';
  readonly pdfFileNamePrefix = 'Dashboard-Servicos';
  readonly dashboardStorageKey = 'dashboard-servicos-sections';

  kpis: Array<KpiCard & { tela: IOpcaoTela }> = [];
  limpezasPorMes = LIMPEZAS_POR_MES;
  limpezasPorTerminal = LIMPEZAS_POR_TERMINAL;
  limpezasPorTransportadora = LIMPEZAS_POR_TRANSPORTADORA;
  limpezasAgendadasProximosDias: { label: string; value: number }[] = [];
  mediaMensalLabels = MEDIA_MENSAL_SERVICOS.labels;
  mediaMensalSeries: LineSeries[] = MEDIA_MENSAL_SERVICOS.series;
  formaPagamento: PieSlice[] = FORMA_PAGAMENTO;
  vendasPorProdutoQuantidade: { label: string; value: number }[] = [];
  vendasPorProdutoValor: { label: string; value: number }[] = [];

  constructor(
    fb: FormBuilder,
    storageService: StorageService,
    private dashboardService: DashboardService
  ) {
    super(fb, storageService);
    const fim = moment().endOf('day');
    const inicio = moment().startOf('month').startOf('day');
    this.form.patchValue({
      DT_INIC: inicio.format('YYYY-MM-DDTHH:mm'),
      DT_FIM: fim.format('YYYY-MM-DDTHH:mm')
    });
  }

  ngOnInit(): void {
    this.initSectionCollapse({
      kpis: false,
      charts: false,
      sales: false,
      payment: false
    });
    this.loadData();
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => this.loadData());
  }

  podeVerGrafico(tela: IOpcaoTela): boolean {
    return validarPermissao(tela, 'consultar');
  }

  temIndicadores(): boolean {
    return this.kpisVisiveis().length > 0;
  }

  kpisVisiveis(): Array<KpiCard & { tela: IOpcaoTela }> {
    return this.kpis.filter(kpi => this.podeVerGrafico(kpi.tela));
  }

  temGraficosOperacionais(): boolean {
    return (
      this.podeVerGrafico('graficoLimpezasPorMes') ||
      this.podeVerGrafico('graficoLimpezasPorTerminal') ||
      this.podeVerGrafico('graficoLimpezasPorTransportadora') ||
      this.podeVerGrafico('graficoLimpezasAgendadas') ||
      this.podeVerGrafico('graficoMediaMensalServico')
    );
  }

  temGraficosVendas(): boolean {
    return (
      this.podeVerGrafico('graficoVendasQtdTipoServico') ||
      this.podeVerGrafico('graficoVendasValorTipoServico')
    );
  }

  temGraficosPagamento(): boolean {
    return (
      this.podeVerGrafico('graficoDistribuicaoFormaPagamento') ||
      this.podeVerGrafico('graficoValoresFormaPagamento')
    );
  }

  override lineChartPoints(values: number[]): string {
    return super.lineChartPoints(values, this.mediaMensalLabels);
  }

  chartHoverGuideX(): number {
    return super.lineHoverGuideX(this.mediaMensalLabels);
  }

  chartHoverZoneX(index: number): number {
    return super.lineHoverZoneX(index, this.mediaMensalLabels);
  }

  chartHoverZoneWidth(): number {
    return super.lineHoverZoneWidth(this.mediaMensalLabels);
  }

  chartTooltipLeftPercent(): number {
    return super.lineTooltipLeftPercent(this.mediaMensalLabels);
  }

  pointOnLineChart(value: number, index: number): { x: number; y: number } {
    return super.lineChartPointForSeries(
      value,
      index,
      this.mediaMensalSeries,
      this.mediaMensalLabels
    );
  }

  private loadData(): void {
    this.isLoading = true;
    this.dashboardService
      .getDashboardServicos({
        DT_INIC: this.form.controls['DT_INIC'].value,
        DT_FIM: this.form.controls['DT_FIM'].value
      })
      .subscribe({
        next: data => this.applyResponse(data),
        error: () => {
          this.isLoading = false;
        }
      });
  }

  private applyResponse(data: IGetDashboardServicosResponse): void {
    if (!data) {
      this.isLoading = false;
      return;
    }

    const limpezasPorMes = data.LimpezasPorMes ?? [];
    const limpezasPorTerminal = data.LimpezasPorTerminal ?? [];
    const limpezasPorTransportadora = data.LimpezasPorTransportadora ?? [];
    const limpezasAgendadasProximosDias = data.LimpezasAgendadasProximosDias ?? [];
    const mediaMensal = data.MediaMensalServicos ?? { Labels: [], Series: [] };
    const formaPagamento = data.FormaPagamento ?? [];
    const vendasPorProduto = data.VendasPorProduto ?? [];

    this.kpis = [
      {
        label: 'Limpezas no período',
        value: data.TotalLimpezas.toLocaleString('pt-BR'),
        hint: 'Limpeza agendada + caminhões, mesma regra do Financeiro',
        tela: 'graficoServicosIndicadorLimpezas'
      },
      {
        label: 'Agendamentos de limpeza',
        value: (data.TotalAgendamentosLimpeza ?? 0).toLocaleString('pt-BR'),
        hint: 'Limpeza agendada no período, mesma regra do Financeiro',
        tela: 'graficoServicosIndicadorAgendamentos'
      },
      {
        label: 'Média de caminhões/dia',
        value: data.MediaDiaria.toLocaleString('pt-BR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1
        }),
        hint: 'Limpezas/dia no período',
        tela: 'graficoServicosIndicadorMediaCaminhoes'
      },
      {
        label: 'Terminais ativos',
        value: String(data.TerminaisAtivos),
        hint: 'Com movimentação',
        tela: 'graficoServicosIndicadorTerminaisAtivos'
      },
      {
        label: 'Receita no período',
        value: this.formatCurrency(data.ReceitaTotal),
        hint: 'Serviços no período',
        tela: 'graficoServicosIndicadorReceita'
      }
    ];

    this.limpezasPorMes = limpezasPorMes.map(item => ({
      label: item.Label,
      value: Number(item.Value)
    }));
    this.limpezasPorTerminal = limpezasPorTerminal.map(item => ({
      label: item.Label,
      value: Number(item.Value)
    }));
    this.limpezasPorTransportadora = limpezasPorTransportadora.map(item => ({
      label: item.Label,
      value: Number(item.Value)
    }));
    this.limpezasAgendadasProximosDias = limpezasAgendadasProximosDias.map(item => ({
      label: item.Label,
      value: Number(item.Value)
    }));
    this.mediaMensalLabels = mediaMensal.Labels ?? [];
    this.mediaMensalSeries = (mediaMensal.Series ?? []).map(series => ({
      name: series.Name,
      color: series.Color,
      values: (series.Values ?? []).map(v => Number(v))
    }));
    this.formaPagamento = formaPagamento.map(slice => ({
      label: slice.Label,
      value: Number(slice.Value),
      color: slice.Color
    }));
    this.vendasPorProdutoQuantidade = vendasPorProduto.map(item => ({
      label: item.Label,
      value: Number(item.Quantidade)
    }));
    this.vendasPorProdutoValor = vendasPorProduto.map(item => ({
      label: item.Label,
      value: Number(item.ValorTotal)
    }));
    this.isLoading = false;
  }
}
